import { createSelector } from 'reselect';
import { AppState } from '../../app-state';
import {
  AnnotationDiff,
  AnnotationDiffResult,
} from '../models/annotation-diff.model';
import { AnnotationResource } from '../resources/annotation.resource';
import { AnnotationRelationResource } from '../resources/annotation-relation.resource';
import { Annotation } from '../models/annotation.model';
import { AnnotationRelation } from '../models/annotation-relation.model';
import { AnnotationTableRow } from '../models/annotation-table-row.model';
import { RELATION_TYPES } from '../../lib/relations/relation-types';
import { Granularity } from '../actions/version-history.actions';
import { AnnotationSaveEvent } from '../models/annotation-save-event.model';
import {
  selectAnnotations,
  selectAnnotationsArray,
} from './annotation.selectors';
import {
  selectAnnotationRelationsArray,
  selectFlatTree,
} from './annotation-relation.selectors';
import {
  selectRelationRuleConditionsArray,
  selectRelationRulesByName,
} from './annotation-schema.selectors';

export const selectVersionHistoryState = (state: AppState) =>
  state.versionHistory;

export const selectVersionHistorySaveEvents = createSelector(
  selectVersionHistoryState,
  vh => vh.saveEvents,
);

export const selectVersionHistoryLoading = createSelector(
  selectVersionHistoryState,
  vh => vh.loading,
);

export const selectSelectedSaveEventId = createSelector(
  selectVersionHistoryState,
  vh => vh.selectedSaveEventId,
);

export const selectDiffModeEnabled = createSelector(
  selectVersionHistoryState,
  vh => vh.diffModeEnabled,
);

export const selectDiffFilterActive = createSelector(
  selectVersionHistoryState,
  vh => vh.diffFilterActive,
);

export const selectGranularity = createSelector(
  selectVersionHistoryState,
  vh => vh.granularity,
);

export function filterSaveEvents(
  saveEvents: AnnotationSaveEvent[],
  granularity: Granularity,
): AnnotationSaveEvent[] {
  if (granularity === 'all') return saveEvents;

  const seen = new Set<string>();
  return saveEvents.filter(event => {
    const date = new Date(event.annotationTimestamp);
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();
    const key =
      granularity === 'daily'
        ? `${y}-${m}-${d}`
        : `${y}-${m}-${d}-${date.getHours()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export const selectFilteredSaveEvents = createSelector(
  [selectVersionHistorySaveEvents, selectGranularity],
  filterSaveEvents,
);

export const selectIsViewingHistory = createSelector(
  selectSelectedSaveEventId,
  id => id !== null,
);

export const selectSelectedVersionIndex = createSelector(
  [selectVersionHistorySaveEvents, selectSelectedSaveEventId],
  (saveEvents, selectedId) => {
    if (selectedId === null) return -1;
    return saveEvents.findIndex(se => se.annotationSaveEventId === selectedId);
  },
);

export const selectCompare = createSelector(
  selectVersionHistoryState,
  vh => vh.compare,
);

const emptyDiff: AnnotationDiffResult = {
  entries: {},
  removedAnnotations: [],
  summary: { added: 0, removed: 0, changed: 0, unchanged: 0 },
};

export const selectVersionDiff = createSelector(
  [
    selectCompare,
    selectDiffModeEnabled,
    selectAnnotationRelationsArray,
    (state: AppState) => state.annotations.ids,
    (state: AppState) => state.annotations.metadata,
    (state: AppState) => state.annotations.values,
    (state: AppState) => state.annotations.types,
  ],
  (
    compare,
    diffModeEnabled,
    currentRelations,
    ids,
    metadata,
    values,
    types,
  ): AnnotationDiffResult => {
    if (!diffModeEnabled) return emptyDiff;
    if (compare.status === 'idle' || compare.status === 'loading') {
      return emptyDiff;
    }

    const currentResources: AnnotationResource[] = ids.map(id => {
      const meta = metadata.get(id);
      const value = values.get(id) || '';
      const type = types.get(id) || '';
      return {
        attributeAnnotationId: parseInt(id, 10) || undefined,
        clientId: id,
        annotationType: type,
        startOffset: meta?.start,
        endOffset: meta?.end,
        annotatorName: meta?.annotatedBy || '',
        value,
        nativeText: meta?.nativeText || '',
        discontinuous: meta?.discontinuous || false,
        isMetadata: meta?.start == null && meta?.end == null,
      };
    });

    const currentRelationResources: AnnotationRelationResource[] = currentRelations.map(
      r => ({
        annotationRelationId: r.id,
        relationType: r.rule.type,
        leftAttributeAnnotationId: parseInt(r.left.id, 10) || 0,
        rightAttributeAnnotationId: parseInt(r.right.id, 10) || 0,
      }),
    );

    // 'unavailable' = earliest version, no older version exists — all are added
    const olderAnnotations =
      compare.status === 'loaded' ? compare.annotations : [];
    const olderRelations = compare.status === 'loaded' ? compare.relations : [];

    return AnnotationDiff.compute(
      olderAnnotations,
      currentResources,
      olderRelations,
      currentRelationResources,
    );
  },
);

const REMOVED_ID_PREFIX = 'removed-';

function removedResourceToAnnotation(res: AnnotationResource): Annotation {
  return {
    id: REMOVED_ID_PREFIX + (res.attributeAnnotationId || ''),
    type: res.annotationType,
    value: res.value,
    nativeText: res.nativeText,
    start: res.startOffset,
    end: res.endOffset,
    annotatedBy: res.annotatorName || '',
    annotatedDate: res.annotatedAt,
    discontinuous: res.discontinuous,
    deleted: true,
  };
}

function buildOldParentLookup(
  compareAnnotations: AnnotationResource[],
  compareRelations: AnnotationRelationResource[],
): Map<number, AnnotationResource> {
  const byId = new Map<number, AnnotationResource>();
  for (const a of compareAnnotations) {
    if (a.attributeAnnotationId != null) {
      byId.set(a.attributeAnnotationId, a);
    }
  }
  const parentLookup = new Map<number, AnnotationResource>();
  for (const rel of compareRelations) {
    if (rel.relationType === 'child-of') {
      const parent = byId.get(rel.rightAttributeAnnotationId);
      if (parent) {
        parentLookup.set(rel.leftAttributeAnnotationId, parent);
      }
    }
  }
  return parentLookup;
}

function findMatchingCurrentAnnotation(
  oldParent: AnnotationResource,
  currentAnnotations: Annotation[],
): Annotation | undefined {
  return currentAnnotations.find(
    a =>
      a.type === oldParent.annotationType &&
      a.start === oldParent.startOffset &&
      a.end === oldParent.endOffset,
  );
}

export const selectAnnotationTableRowsWithDeleted = createSelector(
  [
    selectAnnotationsArray,
    selectAnnotationRelationsArray,
    selectFlatTree,
    selectVersionDiff,
    selectCompare,
    selectAnnotations,
    selectRelationRulesByName,
    selectRelationRuleConditionsArray,
  ],
  (
    annotations,
    relations,
    flatTree,
    diff,
    compare,
    annotationsMap,
    rulesByName,
    conditions,
  ): AnnotationTableRow[] => {
    const hasDiff = diff !== emptyDiff;

    const compareAnnotations =
      compare.status === 'loaded' ? compare.annotations : null;
    const compareRelations =
      compare.status === 'loaded' ? compare.relations : null;

    let rows: AnnotationTableRow[];

    if (
      hasDiff &&
      compareAnnotations &&
      compareRelations &&
      diff.removedAnnotations.length > 0
    ) {
      const oldParentLookup = buildOldParentLookup(
        compareAnnotations,
        compareRelations,
      );
      const deletedAnnotations: Annotation[] = [];
      const syntheticRelations: AnnotationRelation[] = [];

      for (const res of diff.removedAnnotations) {
        const deleted = removedResourceToAnnotation(res);
        deletedAnnotations.push(deleted);

        if (res.attributeAnnotationId != null) {
          const oldParent = oldParentLookup.get(res.attributeAnnotationId);
          if (oldParent) {
            const currentParent = findMatchingCurrentAnnotation(
              oldParent,
              annotations,
            );
            if (currentParent) {
              const rule = AnnotationRelation.findApplicableRule(
                deleted.type,
                RELATION_TYPES.CHILD_OF,
                currentParent.type,
                rulesByName,
                conditions,
              );
              if (rule) {
                syntheticRelations.push({
                  id: 'deleted-rel-' + deleted.id,
                  rule,
                  left: deleted,
                  right: currentParent,
                });
              }
            }
          }
        }
      }

      const allAnnotations = annotations.concat(deletedAnnotations);
      const allRelations = relations.concat(syntheticRelations);
      const mergedFlatTree = AnnotationRelation.createKeyedFlatTree(
        allAnnotations,
        allRelations,
      );
      rows = AnnotationTableRow.createMany(allAnnotations, mergedFlatTree);
    } else {
      rows = AnnotationTableRow.createMany(annotations, flatTree);
    }

    if (hasDiff) {
      rows = rows.map(row => {
        if (row.annotation.deleted) {
          return { ...row, diffStatus: 'removed' as const };
        }
        const entry = diff.entries[row.annotationId];
        if (entry) {
          return { ...row, diffStatus: entry.status };
        }
        return row;
      });
    }

    return rows;
  },
);
